<main class="tax-company">
  <header class="tax-company__topbar">
    <a routerLink="/dashboard/tax-accountancy" class="tax-company__brand">
      <span class="tax-company__brand-icon" aria-hidden="true"><i class="fal fa-file-invoice"></i></span>
      <span><strong>ZINT</strong><small>Tax &amp; accountancy</small></span>
    </a>
    <div class="tax-company__top-actions">
      <button type="button" class="tax-company__default-action" [disabled]="savingDefault || defaultSaved" (click)="makeDefaultView()">{{ defaultSaved ? 'Default view saved' : savingDefault ? 'Saving…' : 'Make my default view' }}</button>
      <button type="button" class="tax-company__top-action" (click)="openFullProfile()">Use full profile as default <i class="fal fa-arrow-up-right" aria-hidden="true"></i></button>
    </div>
  </header>

  <div class="tax-company__content">
    <a routerLink="/dashboard/tax-accountancy" class="tax-company__back"><i class="fal fa-arrow-left" aria-hidden="true"></i> Back to prospect search</a>

    @if (loading || loadingError) {
      <company-profile-loading class="tax-company__loading" [state]="loading ? 'loading' : 'error'" subtitle="Bringing together company, accounts and tax signals." iconClass="fal fa-file-invoice" [offeringsBand]="true"></company-profile-loading>
    } @else {
      <company-profile-hero
        class="tax-company__hero"
        eyebrow="ACCOUNT OVERVIEW"
        headingId="tax-company-name"
        [name]="company.company_overview?.name"
        [location]="company.company_overview?.registered_address_lat_long_and_zoom"
        [logoDomain]="logoDomain"
        markerFill="#0f766e">
        <ng-container ngProjectAs="[heroMeta]">
          <span>Company no. {{ company.company_overview?.number || companyNumber }}</span>
          @if (company.company_overview?.company_status) {<span class="tax-company__status" [class.tax-company__status--inactive]="company.company_overview.company_status !== 'Active'">{{ company.company_overview.company_status }}</span>}
          @if (company.company_overview?.latest_account_type) {<span>{{ company.company_overview.latest_account_type }}</span>}
        </ng-container>
        <div class="tax-company__hero-main">
          @if (company.company_overview?.summary) {<p class="tax-company__summary">{{ company.company_overview.summary }}</p>}
          <company-profile-contact-links class="tax-company__links" [overview]="company.company_overview"></company-profile-contact-links>
        </div>
        @if (productsAndServices.length) {
          <company-profile-offerings
            heroAside
            class="tax-company__offerings"
            role="region"
            aria-labelledby="tax-company-offerings-title"
            layout="wrap"
            heading="Products and services"
            iconClass="fal fa-box-open"
            headingId="tax-company-offerings-title"
            listId="tax-company-all-offerings"
            expandLabel="Show all"
            [items]="productsAndServices"
            [previewCount]="6"
            [showCount]="true"
            [splitDetails]="true">
          </company-profile-offerings>
        }
        <aside heroAside class="tax-company__hero-aside" aria-label="Accounts calendar">
          <span class="tax-company__eyebrow">ACCOUNTS CALENDAR</span>
          <div><span>Year-end reference date</span><strong>{{ company.company_overview?.financial_year_ends || 'Not recorded' }}</strong></div>
          <div><span>Latest accounts period ended</span><strong>{{ latestAccountsPeriod ? (latestAccountsPeriod | date:'d MMM y') : 'Not available' }}</strong></div>
          <div><span>Latest accounts submitted</span><strong>{{ latestAccountsSubmitted ? (latestAccountsSubmitted | date:'d MMM y') : 'Not available' }}</strong></div>
          @if (hasFinancialReport) {
            <button type="button" class="tax-company__report-button" aria-haspopup="dialog" (click)="openFinancialReport()">
              <i class="fal fa-file-spreadsheet" aria-hidden="true"></i>
              <strong>View financial report</strong>
              <i class="fal fa-arrow-up-right" aria-hidden="true"></i>
            </button>
          } @else {
            <p class="tax-company__report-none">No filed figures are available for a financial report.</p>
          }
        </aside>
      </company-profile-hero>

      <section class="tax-company__facts" aria-label="Company at a glance">
        <div><span>REVENUE</span><strong>{{ company.company_overview?.revenue || '—' }}</strong><small>{{ company.company_overview?.is_revenue_estimated ? 'Estimated revenue' : 'Latest available' }}</small></div>
        <div><span>HEADCOUNT</span><strong>{{ company.company_overview?.headcount?.total ?? '—' }}</strong><small>{{ company.company_overview?.headcount?.is_estimated ? 'Estimated employees' : 'Employees' }}</small></div>
        <div><span>COMPANY AGE</span><strong>{{ company.company_overview?.age || '—' }}</strong><small>Registered history</small></div>
        <div><span>PRIMARY INDUSTRY</span><strong>{{ company.company_overview?.sic_codes?.[0]?.section || '—' }}</strong><small>{{ company.company_overview?.sic_codes?.[0]?.description || 'No SIC description recorded' }}</small></div>
      </section>

      <section class="tax-company__play-section" aria-labelledby="tax-company-play-title">
        <div class="tax-company__section-heading"><div><span class="tax-company__eyebrow">SERVICE VIEW</span><h2 id="tax-company-play-title">Focus the account review</h2></div></div>
        <div class="tax-company__play-list" role="group" aria-label="Choose a tax or accountancy service view">
          @for (play of plays; track play.id) {
            <button type="button" [class.is-active]="selectedPlay === play.id" [attr.aria-pressed]="selectedPlay === play.id" (click)="selectPlay(play.id)">
              <strong>{{ play.label }}</strong><small>{{ play.description }}</small>
              @if (insightCount(play.id); as count) {<span class="tax-company__play-ai"><i class="fal fa-sparkles" aria-hidden="true"></i> {{ count }} AI {{ count === 1 ? 'analysis' : 'analyses' }}</span>}
            </button>
          }
        </div>
      </section>

      <section class="tax-company__evidence tax-company__panel" aria-labelledby="tax-company-evidence">
        <div class="tax-company__section-heading"><div><span class="tax-company__kind tax-company__kind--recorded"><i class="fal fa-file-lines" aria-hidden="true"></i> RECORDED DATA</span><h2 id="tax-company-evidence">{{ selectedPlayLabel }} evidence</h2></div></div>
        @if (hasActiveKeyFigures && activeKeyFigures; as keyFigures) {
          <company-profile-key-figures class="tax-company__key-figures" [strip]="keyFigures" [canOpenReport]="hasFinancialReport" (openReport)="openFinancialReport($event)" (figureAction)="onKeyFigureAction($event)"></company-profile-key-figures>
        }
        @if (visibleFacts.length) {
          @if (hasActiveKeyFigures) {<h3 class="tax-company__evidence-subheading">Other recorded evidence</h3>}
          <div class="tax-company__evidence-grid">
            @for (fact of visibleFacts; track $index) {
              <article class="tax-company__evidence-card" [class.tax-company__evidence-card--action]="fact.history">
                <span class="tax-company__evidence-label">{{ fact.label }}</span>
                <strong>{{ fact.value }}</strong>
                <div class="tax-company__evidence-source">
                  @if (fact.period) {<span>{{ periodLabel(fact.period) }}</span>}
                  @if (fact.sourceUrl) {<a [href]="fact.sourceUrl" target="_blank" rel="noopener noreferrer">{{ fact.sourceLabel }} <i class="fal fa-arrow-up-right" aria-hidden="true"></i></a>}
                  @else {<span>{{ fact.sourceLabel }}</span>}
                </div>
                @if (fact.note) {<p>{{ fact.note }}</p>}
                @if (fact.history) {
                  <!-- The button's hit area covers the card, so the whole card opens the history; the source link sits above it. -->
                  <button type="button" class="tax-company__evidence-action" aria-haspopup="dialog" (click)="openHistory(fact.history)">
                    <span>{{ fact.actionLabel }}</span><i class="fal fa-arrow-right" aria-hidden="true"></i>
                  </button>
                }
              </article>
            }
          </div>
        } @else if (!hasActiveKeyFigures) {
          <p class="tax-company__empty">No service-specific evidence is shown from the available records. The full profile may contain more detail. Missing records do not prove that the company has no relevant activity.</p>
        }
        @if (selectedPlay === 'energy' && !hasEnergyAccess) {
          <p class="tax-company__evidence-footnote">Recorded energy figures are available only with your team's energy-data access.</p>
        } @else if (selectedPlay === 'energy' && !hasRecordedEnergy) {
          <p class="tax-company__evidence-footnote">No recorded energy figures are available in this view. This does not indicate that the company has no energy use.</p>
        }
        @if (selectedPlay === 'international_tax') {
          <p class="tax-company__evidence-footnote">This view does not confirm the immediate parent's country, connected-party transactions, transfer-pricing documentation or a tax liability. Geographic revenue and trade records may be incomplete or lack a source period.</p>
        }
      </section>

      <section class="tax-company__panel tax-company__ai" aria-labelledby="tax-company-ai-title" [attr.aria-busy]="insightsState === 'loading'">
        <div class="tax-company__section-heading">
          <div><span class="tax-company__kind tax-company__kind--ai"><i class="fal fa-sparkles" aria-hidden="true"></i> MAGIC INSIGHTS · AI ANALYSIS</span><h2 id="tax-company-ai-title">{{ selectedPlayLabel }} analysis</h2></div>
          @if (insightsState === 'ready' && activeInsightGroups.length) {<span class="tax-company__ai-count">{{ activeInsightGroups.length }} {{ activeInsightGroups.length === 1 ? 'analysis' : 'analyses' }}</span>}
        </div>
        <p class="tax-company__ai-disclosure"><i class="fal fa-wand-magic-sparkles" aria-hidden="true"></i><span><strong>AI estimates, not recorded data.</strong> ZINT Magic Insights reads the company's accounts, website and news to estimate likelihoods, values and activity. Answers may be incomplete or wrong, so check the cited sources before relying on them.</span></p>

        @if (insightsState === 'loading') {
          <div class="tax-company__ai-state" role="status"><zint-loader size="small"></zint-loader><span>Gathering the Magic Insights analysis…</span></div>
        } @else if (insightsState === 'error') {
          <div class="tax-company__ai-state" role="alert"><i class="fal fa-triangle-exclamation" aria-hidden="true"></i><span>The AI analysis could not be loaded.</span><button type="button" (click)="retryInsights()">Try again</button></div>
        } @else if (insightsState === 'unavailable') {
          <div class="tax-company__ai-state"><i class="fal fa-lock" aria-hidden="true"></i><span>Magic Insights for Tax and Accountancy are not included in your team's plan.</span></div>
        } @else if (!activeInsightGroups.length) {
          <div class="tax-company__ai-state"><i class="fal fa-sparkles" aria-hidden="true"></i><span>{{ activeInsightQuestionCount ? 'No AI analysis has been generated for this company and service yet.' : 'Your team does not have Magic Insights questions for this service.' }}</span></div>
        } @else {
          @for (block of activeInsightBlocks; track block.id) {
            @if (block.group; as group) {
              <section class="tax-company__ai-group" [attr.aria-labelledby]="'tax-ai-' + group.id">
                <header class="tax-company__ai-group-head">
                  <h3 [id]="'tax-ai-' + group.id">{{ group.label }}</h3>
                  <span class="tax-company__ai-meta">
                    <span class="tax-company__ai-confidence" [attr.title]="'How confident the analysis is in this answer'"><i class="fas fa-circle" [style.color]="confidenceColour(group.confidence)" aria-hidden="true"></i> {{ confidenceLabel(group.confidence) }}</span>
                    @if (group.generated) {<span>Analysed {{ formatInsightDate(group.generated) }}</span>}
                    <button type="button" class="tax-company__ai-sources" aria-haspopup="dialog" (click)="openInsight(group)" [attr.aria-label]="'View sources for ' + group.label"><i class="fal fa-link" aria-hidden="true"></i> {{ sourceNote(group) }}</button>
                  </span>
                </header>
                @if (group.unknown) {
                  <p class="tax-company__ai-unknown">The analysis could not reach a conclusion for this company. Open the sources to see what was reviewed.</p>
                } @else if (!group.cards.length) {
                  <p class="tax-company__ai-unknown">No answer details were returned for this question.</p>
                } @else {
                  <div class="tax-company__ai-grid">
                    @for (card of group.cards; track card.key) {
                      <company-profile-insight-card [class]="'tax-company__ai-span-' + card.span" [insight]="card" [showConfidence]="false" [footNote]="sourceNote(group)" evidenceLabel="View sources" (viewEvidence)="openInsight(group, $event)"></company-profile-insight-card>
                    }
                  </div>
                }
              </section>
            } @else {
              <div class="tax-company__ai-group tax-company__ai-grid">
                @for (single of block.singles; track single.group.id) {
                  <company-profile-insight-card [class]="'tax-company__ai-span-' + single.card.span" [insight]="single.card" evidenceLabel="View sources" (viewEvidence)="openInsight(single.group, $event)"></company-profile-insight-card>
                }
              </div>
            }
          }
        }
      </section>

      <div class="tax-company__detail-grid">
        <section class="tax-company__panel" aria-labelledby="tax-company-advisers">
          <div class="tax-company__section-heading"><div><span class="tax-company__eyebrow">ACCESS &amp; ADVISERS</span><h2 id="tax-company-advisers">People and professional firms</h2></div></div>
          @if (selectedPlay === 'accounts_audit') {
            <p class="tax-company__subnote tax-company__subnote--lead">The accountant, auditor and banker are shown with the Accounts and audit key figures above.</p>
          } @else {
            <div class="tax-company__adviser-grid">
              @for (adviser of advisers; track adviser.role) {
                <div><span>{{ adviser.role | uppercase }}</span><strong>{{ adviser.name || 'Not recorded' }}</strong>@if (adviser.name) {<small>{{ adviser.source }}</small>}</div>
              }
            </div>
            <p class="tax-company__subnote">The recorded firms may differ from the current advisers. Check the filing before making an introduction.</p>
          }
          @if (people.length) {
            <div class="tax-company__people">
              @for (person of (showAllPeople ? people : people.slice(0, 6)); track $index) {
                <article class="tax-company__person">
                  <span class="tax-company__person-icon" aria-hidden="true"><i class="fal fa-user"></i></span>
                  <div class="tax-company__person-details">
                    <strong>{{ person.name }}
                      @for (code of person.nationalityCodes; track code) {
                        <small class="flag-icon tax-company__person-flag" [ngClass]="'flag-icon-' + code" aria-hidden="true"></small>
                      }
                    </strong>
                    @if (person.roles.length) {
                      <div class="tax-company__person-roles">
                        @for (role of person.roles; track role) {<span>{{ role }}</span>}
                      </div>
                    }
                    @if (person.nationalities || person.age !== undefined || person.appointedOn) {
                      <div class="tax-company__person-facts">
                        @if (person.nationalities) {<span>Nationality: {{ person.nationalities }}</span>}
                        @if (person.age !== undefined) {<span>Age: {{ person.age }}</span>}
                        @if (person.appointedOn) {<span>Appointed {{ person.appointedOn }}</span>}
                      </div>
                    }
                  </div>
                  @if (person.linkedInUrl || person.informationUrl) {
                    <div class="tax-company__person-links">
                      @if (person.linkedInUrl) {
                        <a [href]="person.linkedInUrl" target="_blank" rel="noopener noreferrer"
                          [attr.aria-label]="(person.linkedInIsDirect ? 'View LinkedIn profile for ' : 'Search LinkedIn for ') + person.name"
                          [attr.title]="person.linkedInIsDirect ? 'View LinkedIn profile' : 'Search LinkedIn'">
                          <i class="fab fa-linkedin-in" aria-hidden="true"></i>
                          @if (!person.linkedInIsDirect) {<i class="fal fa-magnifying-glass tax-company__person-link-search" aria-hidden="true"></i>}
                        </a>
                      }
                      @if (person.informationUrl) {<a [href]="person.informationUrl" target="_blank" rel="noopener noreferrer" [attr.aria-label]="'More about ' + person.name"><i class="fal fa-arrow-up-right" aria-hidden="true"></i></a>}
                    </div>
                  }
                </article>
              }
            </div>
            @if (people.length > 6) {
              <button class="tax-company__people-toggle" type="button" (click)="showAllPeople = !showAllPeople">
                {{ showAllPeople ? 'Show fewer people' : 'Show all ' + people.length + ' people' }}
                <i class="fal" [class.fa-chevron-down]="!showAllPeople" [class.fa-chevron-up]="showAllPeople" aria-hidden="true"></i>
              </button>
            }
          } @else {<p class="tax-company__empty">No decision makers are recorded in this view.</p>}
        </section>

        <section class="tax-company__panel" aria-labelledby="tax-company-context">
          <div class="tax-company__section-heading"><div><span class="tax-company__eyebrow">COMPANY CONTEXT</span><h2 id="tax-company-context">Entity and location</h2></div></div>
          <div class="tax-company__context-row"><span>Registered address</span><strong>{{ company.company_overview?.registered_address || 'Not recorded' }}</strong></div>
          @if (company.company_overview?.trading_addresses?.length) {
            <div class="tax-company__context-row"><span>Trading locations</span><strong>{{ company.company_overview.trading_addresses.length }} recorded</strong></div>
          }
          @if (company.company_overview?.sic_codes?.length) {
            <div class="tax-company__context-row"><span>SIC classifications</span><ul>@for (sic of company.company_overview.sic_codes; track sic.code) {<li><strong>{{ sic.code }}</strong> {{ sic.description }}</li>}</ul></div>
          } @else {<div class="tax-company__context-row"><span>SIC classifications</span><strong>Not recorded</strong></div>}
          @if (company.company_overview?.is_in_group) {<p class="tax-company__subnote">This company is recorded as part of a group. Review the legal entity and ownership details in the full profile before attributing activity to it.</p>}
        </section>
      </div>

      @if (hasPAFAccess) {
        <section class="tax-company__panel tax-company__land" aria-labelledby="tax-company-land">
          <div class="tax-company__section-heading">
            <div><span class="tax-company__eyebrow">LICENSED PROPERTY DATA</span><h2 id="tax-company-land">Land ownership and EPC</h2></div>
            <p>Match the title and certificate to this legal entity before relying on the property details.</p>
          </div>
          @if (landOwnershipLoading) {
            <p class="tax-company__empty" role="status">Loading land and EPC records…</p>
          } @else if (landOwnershipData.length) {
            <div class="tax-company__land-grid">
              @for (address of landOwnershipData; track address.title_number || $index) {
                <article class="tax-company__land-card">
                  <div class="tax-company__land-heading"><strong>{{ landAddressText(address) }}</strong>@if (address.title_number) {<span>Title {{ address.title_number }}</span>}</div>
                  <div class="tax-company__land-facts">
                    @if (address.tenure) {<span>Tenure <strong>{{ address.tenure }}</strong></span>}
                    @if (address.classification) {<span>Category <strong>{{ address.classification }}</strong></span>}
                    @if (address.price_paid != null) {<span>Price paid <strong>{{ address.price_paid | currency:'GBP':'symbol':'1.0-0' }}</strong></span>}
                    @if (address.date_proprietor_added) {<span>Ownership recorded <strong>{{ address.date_proprietor_added | date:'d MMM y' }}</strong></span>}
                  </div>
                  @if (address.epc_data?.length) {
                    <div class="tax-company__epc">
                      <h3>EPC certificates</h3>
                      @for (certificate of address.epc_data; track $index) {
                        <div class="tax-company__epc-row">
                          <strong>{{ certificate.report_type || 'Certificate' }}</strong>
                          @if (certificate.property_type) {<span>{{ certificate.property_type }}</span>}
                          @if (certificate.asset_rating_band) {<span>Rating {{ certificate.asset_rating_band }}</span>}
                          @else if (certificate.asset_rating != null) {<span>Asset rating {{ certificate.asset_rating }}</span>}
                          @if (certificate.floor_area != null) {<span>{{ certificate.floor_area | number:'1.0-0' }} m²</span>}
                          @if (certificate.lodgement_date) {<small>Lodged {{ certificate.lodgement_date | date:'d MMM y' }}</small>}
                        </div>
                      }
                    </div>
                  }
                </article>
              }
            </div>
          } @else {
            <p class="tax-company__empty">No matched land title or EPC records are available for this company.</p>
          }
          <p class="tax-company__land-licence">Land ownership and EPC data is derived from Royal Mail PAF® and provided under licence. It is subject to <a href="https://drive.google.com/file/d/1OaqjlmvEiLQ1XebSl06Lg4oQyZdO-kt3/view?usp=drive_link" target="_blank" rel="noopener noreferrer">these terms</a> and must not be held out as a comprehensive address database. HM Land Registry data © Crown copyright and database right 2021, licensed under the Open Government Licence v3.0.</p>
        </section>
      }

      <section class="tax-company__panel tax-company__activity" aria-labelledby="tax-company-activity">
        <div class="tax-company__section-heading">
          <div><span class="tax-company__eyebrow">DATED ACTIVITY</span><h2 id="tax-company-activity">Sources to review</h2></div>
          <p>Recent public material can explain timing; a headline alone does not prove a tax opportunity.</p>
        </div>
        @if (hasActivity) {
          <div class="tax-company__activity-grid">
            @if (articles.length) {
              <div class="tax-company__activity-group"><h3>News and company updates</h3>
                @for (article of articles.slice(0, 4); track $index) {
                  <article><small>{{ article.source }}@if (article.date) { · {{ article.date | date:'d MMM y' }}}</small>
                    @if (article.url) {<a [href]="article.url" target="_blank" rel="noopener noreferrer">{{ article.title }} <i class="fal fa-arrow-up-right" aria-hidden="true"></i></a>}
                    @else {<strong>{{ article.title }}</strong>}
                  </article>
                }
              </div>
            }
            @if (visibleHiring.length) {
              <div class="tax-company__activity-group"><h3>Recent hiring</h3>
                @for (job of visibleHiring; track $index) {
                  <article><small>Job listing@if (job.date_published) { · {{ job.date_published }}}</small>
                    @if (externalUrl(job.link); as jobUrl) {<a [href]="jobUrl" target="_blank" rel="noopener noreferrer">{{ job.title || 'Role recorded' }} <i class="fal fa-arrow-up-right" aria-hidden="true"></i></a>}
                    @else {<strong>{{ job.title || 'Role recorded' }}</strong>}
                  </article>
                }
              </div>
            }
            @if (visibleGrants.length) {
              <div class="tax-company__activity-group"><h3>Recorded grants</h3>
                @for (grant of visibleGrants; track $index) {
                  <article><small>Grant project@if (grant.project_start_date) { · Starts {{ grant.project_start_date | date:'d MMM y' }}}</small><strong>{{ grant.project_title || 'Project recorded' }}</strong></article>
                }
              </div>
            }
            @if (visiblePatents.length) {
              <div class="tax-company__activity-group"><h3>Patent filings</h3>
                @for (patent of visiblePatents; track $index) {
                  <article><small>Patent record@if (patent.date_filed) { · Filed {{ patent.date_filed | date:'d MMM y' }}}</small>
                    @if (externalUrl(patent.ipo_link); as patentUrl) {<a [href]="patentUrl" target="_blank" rel="noopener noreferrer">{{ patent.title || 'Patent recorded' }} <i class="fal fa-arrow-up-right" aria-hidden="true"></i></a>}
                    @else {<strong>{{ patent.title || 'Patent recorded' }}</strong>}
                  </article>
                }
              </div>
            }
          </div>
        } @else {<p class="tax-company__empty">No dated news, hiring, grant or accessible patent records are available in this view.</p>}
      </section>

      @if (hasMagicInsightsAccess) {
        <section class="tax-company__insights" aria-labelledby="tax-company-insights">
          <div><span class="tax-company__eyebrow">FULL PROFILE</span><h2 id="tax-company-insights">Ask your own Magic Insights questions</h2><p>This view shows ZINT's standard Tax and Energy analysis. Your team's custom questions, other modules and new runs are in the full profile. Opening it also makes the full profile your default view.</p></div>
          <button type="button" (click)="openFullProfile()">Open the full profile <i class="fal fa-arrow-up-right" aria-hidden="true"></i></button>
        </section>
      }
    }
  </div>

  @if (financialReportOpen) {
    <company-profile-dialog
      [eyebrow]="company.company_overview?.name"
      heading="Financial report"
      [intro]="financialPeriodsText(financialPeriodCount) + ', newest first. Figures are extracted from the filed accounts, so check the filing before relying on them.'"
      closeLabel="Close financial report"
      (closed)="closeFinancialReport()">
      <company-profile-financial-report class="tax-company__dialog-fill" [accounts]="company.financials?.accounts" [assetBreakdown]="company.asset_breakdown" periods="latest-five" [tab]="financialTab" (tabChange)="financialTab = $event"></company-profile-financial-report>
    </company-profile-dialog>
  }

  @if (historyOpen) {
    <company-profile-dialog
      [size]="historyOpen === 'energy' ? 'wide' : 'medium'"
      [eyebrow]="company.company_overview?.name"
      [heading]="historyHeading"
      [intro]="historyIntro"
      [closeLabel]="'Close ' + historyHeading.toLowerCase()"
      (closed)="closeHistory()">
      <tax-profile-history class="tax-company__dialog-fill" [kind]="historyOpen" [patents]="historyPatents" [grants]="historyGrants" [energy]="historyEnergy"></tax-profile-history>
    </company-profile-dialog>
  }

  @if (selectedInsight && selectedInsightGroup) {
    <company-profile-evidence-dialog
      eyebrow="MAGIC INSIGHTS · AI ANALYSIS"
      [heading]="selectedInsight.title"
      [subtitle]="selectedInsightGroup.label + ' · AI estimate, check the sources below'"
      [response]="selectedInsightGroup.response"
      answerHeading="AI answer"
      commentaryHeading="AI reasoning"
      referencesHeading="Cited sources"
      closeLabel="Close sources"
      [detailedReferences]="true"
      (closed)="closeInsight()">
      <company-profile-insight-answer evidenceAnswer [insight]="selectedInsight" [expanded]="true"></company-profile-insight-answer>
    </company-profile-evidence-dialog>
  }
</main>
